import { GroupPrivacy } from '@ms/yammer-draft';

import { Entity, EntityMap, Group, LiveEvent, Message, Network, Thread, ThreadScope, User } from '../types';

interface BaseNotification extends Entity {
  readonly createdAt: number;
  readonly isUnseen: boolean;
  readonly subtype: NotificationSubtype;
}

export enum NotificationSubtype {
  ReactionNotification = 'ReactionNotification',
  MarkedBestReplyMyReplyNotification = 'MarkedBestReplyMyReplyNotification',
  MarkedBestReplyMyThreadNotification = 'MarkedBestReplyMyThreadNotification',
  MarkedBestReplyFollowedThreadNotification = 'MarkedBestReplyFollowedThreadNotification',
  StartedBroadcastEventNotification = 'StartedBroadcastEventNotification',
  RequestedGroupJoinNotification = 'RequestedGroupJoinNotification',
  AddedToGroupNotification = 'AddedToGroupNotification',
  InvitedToGroupAsGuestNotification = 'InvitedToGroupAsGuestNotification',
  ChangedGroupPrivacyNotification = 'ChangedGroupPrivacyNotification',
  FollowedByUserNotification = 'FollowedByUserNotification',
  UpvotedQuestionReplyNotification = 'UpvotedQuestionReplyNotification',
  PostedMessageOnBehalfOfUserNotification = 'PostedMessageOnBehalfOfUserNotification',
  PollSummaryNotification = 'PollSummaryNotification',
  UserWallMessagePostNotification = 'UserWallMessagePostNotification',
  UserMomentMessagePostNotification = 'UserMomentMessagePostNotification',
  TeamsMeetingThreadCreatedNotification = 'TeamsMeetingThreadCreatedNotification',
  TeamsMeetingModeratedThreadPublishedNotification = 'TeamsMeetingModeratedThreadPublishedNotification',
}

export interface ReactionNotificationDetails {
  readonly featuredReactionUserIds: User['id'][];
  readonly groupId?: Group['id'];
  readonly groupTelemetryId?: Group['telemetryId'];
  readonly messageId: Message['id'];
  readonly messageTelemetryId: Message['telemetryId'];
  readonly reactingUsersCount: number;
  readonly threadId: Thread['id'];
  readonly threadTelemetryId: Thread['telemetryId'];
  readonly isThreadStarter: boolean;
  readonly scope: ThreadScope;
}

export interface ReactionNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.ReactionNotification;
  readonly details: ReactionNotificationDetails;
}

export type NotificationMarkedBestReplySubtype =
  | NotificationSubtype.MarkedBestReplyMyReplyNotification
  | NotificationSubtype.MarkedBestReplyMyThreadNotification
  | NotificationSubtype.MarkedBestReplyFollowedThreadNotification;

export interface MarkedBestReplyNotificationDetails {
  readonly markingUserId: User['id'];
  readonly groupId: Group['id'];
  readonly groupTelemetryId: Group['telemetryId'];
  readonly messageId: Message['id'];
  readonly messageTelemetryId: Message['telemetryId'];
  readonly threadId: Thread['id'];
  readonly threadTelemetryId: Thread['telemetryId'];
  readonly wallOwnerName?: User['name'];
  readonly scope: ThreadScope;
}

export interface MarkedBestReplyNotification extends BaseNotification {
  readonly subtype: NotificationMarkedBestReplySubtype;
  readonly details: MarkedBestReplyNotificationDetails;
}

export interface StartedBroadcastEventNotificationDetails {
  readonly broadcastId: LiveEvent['broadcastId'];
  readonly groupId: Group['id'];
  readonly groupTelemetryId: Group['telemetryId'];
}

export interface StartedBroadcastEventNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.StartedBroadcastEventNotification;
  readonly details: StartedBroadcastEventNotificationDetails;
}

export interface RequestedGroupJoinNotificationDetails {
  readonly groupId: Group['id'];
  readonly groupTelemetryId: Group['telemetryId'];
  readonly requestingUserId: User['id'];
  readonly isResolved: boolean;
}

export interface RequestedGroupJoinNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.RequestedGroupJoinNotification;
  readonly details: RequestedGroupJoinNotificationDetails;
}

export interface AddedToGroupNotificationDetails {
  readonly groupId: Group['id'];
  readonly groupTelemetryId: Group['telemetryId'];
  readonly addingUserId?: User['id'];
}

export interface AddedToGroupNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.AddedToGroupNotification;
  readonly details: AddedToGroupNotificationDetails;
}

export interface InvitedToGroupAsGuestNotificationDetails {
  readonly groupId: Group['id'];
  readonly groupTelemetryId: Group['telemetryId'];
  readonly networkId: Network['id'];
}

export interface InvitedToGroupAsGuestNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.InvitedToGroupAsGuestNotification;
  readonly details: InvitedToGroupAsGuestNotificationDetails;
}

export interface ChangedGroupPrivacyNotificationDetails {
  readonly fromPrivacy: GroupPrivacy;
  readonly toPrivacy: GroupPrivacy;
  readonly groupId: Group['id'];
  readonly groupTelemetryId: Group['telemetryId'];
  readonly userId: User['id'];
}

export interface ChangedGroupPrivacyNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.ChangedGroupPrivacyNotification;
  readonly details: ChangedGroupPrivacyNotificationDetails;
}

export type GroupNotification =
  | AddedToGroupNotification
  | InvitedToGroupAsGuestNotification
  | ChangedGroupPrivacyNotification
  | RequestedGroupJoinNotification;

export interface FollowedByUserNotificationDetails {
  readonly featuredFollowerUserIds: User['id'][];
  readonly followingUsersCount: number;
}

export interface FollowedByUserNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.FollowedByUserNotification;
  readonly details: FollowedByUserNotificationDetails;
}

export interface UpvotedQuestionReplyNotificationDetails {
  readonly featuredUpvotingUserIds: User['id'][];
  readonly upvotingUsersCount: number;
  readonly messageId: Message['id'];
  readonly threadId: Thread['id'];
  readonly threadTelemetryId: Thread['telemetryId'];
  readonly groupId: Group['id'];
  readonly wallOwnerName?: User['name'];
  readonly wallOwnerId?: User['id'];
}

export interface UpvotedQuestionReplyNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.UpvotedQuestionReplyNotification;
  readonly details: UpvotedQuestionReplyNotificationDetails;
}

export interface PostedMessageOnBehalfOfUserNotificationDetails {
  readonly postingUserId: User['id'];
  readonly onBehalfOfUserId: User['id'];
  readonly viewerIsOnBehalfOfSender: boolean;
  readonly messageId: Message['id'];
  readonly messageTelemetryId: Message['telemetryId'];
  readonly threadId: Thread['id'];
  readonly threadTelemetryId: Thread['telemetryId'];
  readonly groupId: Group['id'];
}

export interface PostedMessageOnBehalfOfUserNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.PostedMessageOnBehalfOfUserNotification;
  readonly details: PostedMessageOnBehalfOfUserNotificationDetails;
}

export interface PollSummaryNotificationDetails {
  readonly votingUsersCount: number;
  readonly threadId: Thread['id'];
  readonly threadTelemetryId: Thread['telemetryId'];
  readonly groupId?: Group['id'];
  readonly wallOwnerId?: User['id'];
}

export interface PollSummaryNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.PollSummaryNotification;
  readonly details: PollSummaryNotificationDetails;
}

export interface UserWallMessagePostNotificationDetails {
  readonly threadId: Thread['id'];
  readonly threadTelemetryId: Thread['telemetryId'];
  readonly isAnnouncement: Thread['isAnnouncement'];
  readonly messageId: Message['id'];
  readonly messageTelemetryId: Message['telemetryId'];
}

export interface UserWallMessagePostNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.UserWallMessagePostNotification;
  readonly details: UserWallMessagePostNotificationDetails;
}

export interface UserMomentMessagePostNotificationDetails {
  readonly threadId: Thread['id'];
  readonly threadTelemetryId: Thread['telemetryId'];
  readonly messageId: Message['id'];
  readonly messageTelemetryId: Message['telemetryId'];
}

export interface UserMomentMessagePostNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.UserMomentMessagePostNotification;
  readonly details: UserMomentMessagePostNotificationDetails;
}

export interface TeamsMeetingThreadCreatedNotificationDetails {
  readonly threadId: Thread['id'];
  readonly threadTelemetryId: Thread['telemetryId'];
  readonly messageId: Message['id'];
  readonly messageTelemetryId: Message['telemetryId'];
  readonly scope: ThreadScope;
}

export interface TeamsMeetingThreadCreatedNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.TeamsMeetingThreadCreatedNotification;
  readonly details: TeamsMeetingThreadCreatedNotificationDetails;
}

export interface TeamsMeetingModeratedThreadPublishedNotificationDetails {
  readonly threadId: Thread['id'];
  readonly threadTelemetryId: Thread['telemetryId'];
  readonly messageId: Message['id'];
  readonly messageTelemetryId: Message['telemetryId'];
  readonly scope: ThreadScope;
}

export interface TeamsMeetingModeratedThreadPublishedNotification extends BaseNotification {
  readonly subtype: NotificationSubtype.TeamsMeetingModeratedThreadPublishedNotification;
  readonly details: TeamsMeetingModeratedThreadPublishedNotificationDetails;
}

export type Notification =
  | FollowedByUserNotification
  | GroupNotification
  | MarkedBestReplyNotification
  | ReactionNotification
  | StartedBroadcastEventNotification
  | PostedMessageOnBehalfOfUserNotification
  | UpvotedQuestionReplyNotification
  | PollSummaryNotification
  | UserWallMessagePostNotification
  | UserMomentMessagePostNotification
  | TeamsMeetingThreadCreatedNotification
  | TeamsMeetingModeratedThreadPublishedNotification;

export type Notifications = EntityMap<Notification>;
